import axios from "axios";
import API_BASE_URL, { IS_LOCAL_API, LIVE_API_BASE_URL } from "../config/apiConfig";
import { getAccessToken, isAdminUser, isLoggedIn } from "../util/auth";

export const getAuthConfig = () => {
  const token = getAccessToken();

  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
  };
};

export const assertAdminAuth = () => {
  if (!isLoggedIn() || !isAdminUser()) {
    window.alert(
      "Unauthorized. Please log in with an admin account to continue.",
    );
    window.location.href = "/login";
    return false;
  }

  return true;
};

export const formatApiDate = (date) => {
  const parsedDate = new Date(date);
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();

  return `${day}-${month}-${year}`;
};

export const getDayName = (date) =>
  new Date(date).toLocaleDateString("en-US", { weekday: "long" });

export const formatDisplayDate = (value) => {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleDateString("en-US");
};

export const getApiErrorMessage = (error, fallback) => {
  const status = error?.response?.status;
  const apiErrors = error?.response?.data?.errors;

  if (status === 401) {
    return "Unauthorized. Please log in again as admin.";
  }

  if (status === 403) {
    return "Forbidden. Admin access is required for this action.";
  }

  if (Array.isArray(apiErrors) && apiErrors.length) {
    const detailedMessages = apiErrors
      .map((entry) => entry?.message)
      .filter(Boolean);

    if (detailedMessages.length) {
      return detailedMessages.join(", ");
    }
  }

  const apiMessage = error?.response?.data?.message;

  if (Array.isArray(apiMessage)) {
    return apiMessage.join(", ");
  }

  const requestUrl = error?.config?.url;
  const apiHint = IS_LOCAL_API
    ? "Ensure cursor_API is running on http://localhost:9000 and restart the React dev server."
    : `Ensure the API route is deployed on ${LIVE_API_BASE_URL}.`;

  if (typeof apiMessage === "string" && apiMessage !== "Not Found") {
    if (status === 404 && requestUrl && /^Cannot (GET|POST|PUT|DELETE)/.test(apiMessage)) {
      return `${apiMessage} at ${requestUrl}. ${apiHint}`;
    }

    return apiMessage;
  }

  const responseData = error?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) {
    const cannotRouteMatch = responseData.match(/Cannot (GET|POST|PUT|DELETE) ([^\s<]+)/);

    if (cannotRouteMatch) {
      const route = cannotRouteMatch[2];
      const method = cannotRouteMatch[1];
      const fullUrl = requestUrl || route;

      return `${method} ${route} failed at ${fullUrl}. ${apiHint}`;
    }

    if (responseData.includes("<!DOCTYPE html>")) {
      return `Unexpected HTML response from ${requestUrl || "the server"}. ${apiHint}`;
    }

    return responseData.trim();
  }

  if (status === 404 && requestUrl) {
    return `API route not found: ${requestUrl}. ${apiHint}`;
  }

  if (
    error?.message === "Network Error" ||
    error?.code === "ERR_NETWORK" ||
    error?.code === "ECONNREFUSED"
  ) {
    return IS_LOCAL_API
      ? "Cannot reach the local API. Start cursor_API on http://localhost:9000, then restart npm start (Ctrl+C → npm start)."
      : fallback || apiHint;
  }

  return error?.message || fallback;
};

export { API_BASE_URL };
